
import {
  Project,
  Project_Activity,
  Project_Activity_Time,
} from "@/generated/graphql";
import { Vue, Component, Prop } from "vue-property-decorator";
import GET_PROJECT_ALL_ACTIVITY_TIMES from "@/apollo/projectActivities/GetProjectAllActivityTimes.gql";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import convertSecondsToReadable from "@/utils/convertSecondsToReadable";
import ManagementTimeTrackerModal from "@/components/projects/timeTracker/ManagementTimeTrackerModalNew.vue";
import DeclineReasonModal from "@/components/projects/timeTracker/DeclineReasonModal.vue";
import MoreActionsClick from "@/components/common/MoreActionsClick.vue";
import S3Image from "@/components/common/s3-image.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import PaginationConfig from "@/entities/PaginationConfig";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholderNew.vue";
import paginationConfig from "@/data/paginationConfig";
import moment from 'moment';
import SchedulerItemAvatar from "@/components/scheduler/SchedulerItemAvatar.vue";
import { hslColorFromName } from "@/utils/hslColorFromName";
import UiButton from "@/components/button/ui-button.vue";

@Component({
  components: {
    LoadingOverlay,
    ManagementTimeTrackerModal,
    DeclineReasonModal,
    S3Image,
    MoreActionsClick,
    EmptyListPlaceholder,
    CardPagination,
    SchedulerItemAvatar,
    UiButton,
  },
  apollo: {
    activitiesTimes: {
      query: GET_PROJECT_ALL_ACTIVITY_TIMES,
      variables() {
        return {
          projectId: this.project?.id,
          completed: this.completedVariable,
        };
      },
      update(data) {
        this.paginationConfig.total = data.project_activity.length;
        let sorted_project_activity = data.project_activity.sort((a, b) => {
          if (
            a.project_activity_times.length === 0 &&
            b.project_activity_times.length === 0
          )
            return 0;
          if (a.project_activity_times.length === 0) return 1;
          if (b.project_activity_times.length === 0) return -1;
          let dateA = new Date(
            a.project_activity_times[
              a.project_activity_times.length - 1
            ].tracked_date
          );
          let dateB = new Date(
            b.project_activity_times[
              b.project_activity_times.length - 1
            ].tracked_date
          );
          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        });
        return sorted_project_activity;
      },
    },
  },
})
export default class AdminTimeTrackerTab extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;

  currentFilter: string = "active";
  activeCount: number = 0;
  completedCount: number = 0;
  activitiesTimes: Project_Activity[] = [];
  processing: boolean = false;
  convertSecondsToReadable = convertSecondsToReadable;
  managementTimeTrackerModal: boolean = false;
  showDeclineReasonModal: boolean = false;
  selectedTime: Project_Activity_Time = null;
  selectedActivity: Project_Activity = null;
  paginationConfig: PaginationConfig = { ...paginationConfig };

activityDate(date){
  if(!date) return 'N/A';
return moment(date).format("DD/MM/YYYY");
}
  get btnActive(): string {
    if (this.currentFilter !== "completed") {
      this.activeCount = this.activitiesTimes.length;
    }
    return this.currentFilter === "active"
      ? " text-black  bg-hover-yellow rounded-full font-normal"
      : " text-gray-300 bg-transparent";
  }

  bgStyle(initials): object {
    return {
      background: hslColorFromName(this.getContractorInitials(initials) || ""),
    };
  }

  get btnCompleted(): string {
    if (this.currentFilter !== "active") {
      this.completedCount = this.activitiesTimes.length;
    }
    return this.currentFilter === "completed"
      ? " text-black bg-hover-yellow rounded-full font-normal"
      : " text-gray-300 bg-transparent";
  }

  get loading(): boolean {
    if (this.processing || this.$apollo.queries.activitiesTimes.loading) {
      return true;
    }
    return false;
  }

  get hasData(): boolean {
    if (this.activitiesTimes && this.activitiesTimes.length > 0) {
      return true;
    }
    return false;
  }

  get overlayClasses(): string {
    if (this.hasData) {
      return "";
    }
    return "2xl:mt-44 lg:mt-24 md:mt-5";
  }

  get completedVariable(): boolean[] {
    if (this.currentFilter === "active") {
      return [false];
    }
    if (this.currentFilter === "completed") {
      return [true];
    }
    return [true, false];
  }

  async refetchData(): Promise<void> {
    await this.$apollo.queries.activitiesTimes.refetch();
  }

  calcApprovedTime(times: Project_Activity_Time[]): string {
    if (!times) {
      return this.convertSecondsToReadable(0);
    }
    let time = 0;
    times.forEach((element) => {
      if (element.status.toUpperCase() === "APPROVED") time += element.time;
    });
    return this.convertSecondsToReadable(time);
  }

  calcDeclinedTime(times: Project_Activity_Time[]): string {
    if (!times) {
      return this.convertSecondsToReadable(0);
    }
    let time = 0;
    times.forEach((element) => {
      if (element.status.toUpperCase() === "DECLINED") time += element.time;
    });
    return this.convertSecondsToReadable(time);
  }

  calcPendingTime(times: Project_Activity_Time[]): string {
    if (!times) {
      return this.convertSecondsToReadable(0);
    }
    let time = 0;
    times.forEach((element) => {
      if (
        element.status.toUpperCase() !== "APPROVED" &&
        element.status.toUpperCase() !== "DECLINED"
      )
        time += element.time;
    });
    return this.convertSecondsToReadable(time);
  }

  calcSubmittedTime(times: Project_Activity_Time[]): string {
    if (!times) {
      return this.convertSecondsToReadable(0);
    }
    let time = 0;
    times.forEach((element) => {
      if (element.status.toUpperCase() === "SUBMITTED") time += element.time;
    });
    return this.convertSecondsToReadable(time);
  }

  getContractorInitials(user: any): string {
    if (!user) {
      return "";
    }
    return (
      (user.given_name
        ? user.given_name.trimStart()[0].toUpperCase() + " "
        : "") +
      (user.family_name ? user.family_name.trimStart()[0].toUpperCase() : "")
    );
  }

  get isActivitiesLoaded(): boolean {
    return this.loading && !this.activitiesTimes;
  }
}
